import { syncNow } from './sync-worker'
import { dbg, dbgWarn } from '../logger'

/**
 * sync-nudge.js - COALESCING del nudge de sync que dispara cada GUARDADO (TKT-2240).
 *
 * PROBLEMA (medido: 2-3 devices, ~6.77 GB/semana): cada respuesta guardada en una auditoria
 * llamaba `syncNow()` de inmediato (el mixin de llenado, OccurrenceTaskCard.vue, OccurrenceTaskCreateDialog.vue,
 * stores/occurrences/actions.js). Con red, un llenado activo produce un guardado cada pocos
 * segundos y CADA uno arrancaba un drain completo; el handler de occurrence.syncOccurrence
 * re-hidrata TODAS las answers+addons de la occurrence y re-arma el multipart, asi que el
 * batch entero (fotos incluidas mientras su binario siga en disco) volaba una vez por guardado.
 * El colapso de siblings (collapse-sibling-sync.js) no mitiga nada con red: nunca hay siblings
 * pending acumulados en ese instante.
 *
 * FIX: los guardados consecutivos COALESCEN en un solo drain. Debounce TRAILING de
 * NUDGE_DEBOUNCE_MS con tope duro NUDGE_MAX_WAIT_MS (si no, un llenado continuo reagendaria
 * para siempre y el nudge no se dispararia nunca). El tope se fija al TICK del worker (30 s):
 * en el peor caso el nudge no tarda mas que el tick periodico, que es el piso que ya existia.
 *
 * QUE NO PASA POR AQUI (siguen inmediatos, a proposito):
 *   - el cierre/finalizacion de la auditoria (`syncNow({ force: true })` en Occurrence.vue y en
 *     el drenado previo al envio de correo): el usuario esta esperando el resultado;
 *   - las firmas (submitOccurrenceUpdate): son 2-3 por auditoria, no amplifican, y van pegadas al cierre;
 *   - el boton "Sincronizar ahora" (TheSyncBanner) y el resume de auth;
 *   - el tick periodico del sync-worker (TICK_INTERVAL 30 s), que es el piso de latencia.
 *
 * CERO PERDIDA: la fila ya esta durable en el outbox ANTES del nudge (offline-FIRST). Retrasar
 * el drain solo retrasa la subida; si la app muere antes de que el timer dispare, el worker la
 * drena en su siguiente tick. El nudge NUNCA lanza ni devuelve una promesa que rechace.
 */

// Ventana de coalescing: los guardados dentro de esta ventana comparten un solo drain.
export const NUDGE_DEBOUNCE_MS = 10000

// Tope duro desde el PRIMER guardado de la rafaga (ver arriba): igual al TICK_INTERVAL del worker.
export const NUDGE_MAX_WAIT_MS = 30000

let timer = null
let firstRequestAt = 0
let pendingRun = null

const defaultRun = () => syncNow()

function fire () {
  timer = null
  firstRequestAt = 0
  const run = pendingRun || defaultRun
  pendingRun = null
  try {
    const result = run()
    if (result && typeof result.catch === 'function') {
      result.catch((e) => dbgWarn('sync', 'nudge coalescido fallo (ignorado)', e))
    }
  } catch (e) {
    dbgWarn('sync', 'nudge coalescido fallo (ignorado)', e)
  }
}

/**
 * Agenda un drain COALESCIDO. Llamarlo N veces seguidas produce UN solo drain.
 *
 * @param {{ run?: Function, delayMs?: number, maxWaitMs?: number }} deps
 *        `run` inyectable (default `syncNow`) para los specs; delay/maxWait idem.
 * @returns {void} fire-and-forget: nunca lanza, nunca devuelve promesa.
 */
export function scheduleSyncNudge ({
  run = defaultRun,
  delayMs = NUDGE_DEBOUNCE_MS,
  maxWaitMs = NUDGE_MAX_WAIT_MS
} = {}) {
  const now = Date.now()
  if (!firstRequestAt) {
    firstRequestAt = now
    dbg('sync', `nudge de sync agendado (coalesce ${delayMs}ms, tope ${maxWaitMs}ms)`)
  }
  pendingRun = run
  // Tope duro: lo que quede de la ventana maxima desde el PRIMER guardado de la rafaga.
  const remainingCap = Math.max(0, (firstRequestAt + maxWaitMs) - now)
  const wait = Math.min(delayMs, remainingCap)
  if (timer) clearTimeout(timer)
  timer = setTimeout(fire, wait)
}

/**
 * Descarta el nudge pendiente. Lo llama stopSyncWorker() en el logout/reset: el timer vive en
 * estado de MÓDULO, así que sin esto podía disparar hasta 30 s después de cerrar sesión y arrancar
 * un drain con los tokens ya borrados (401 -> authPaused hasta el siguiente login).
 */
export function cancelSyncNudge () {
  if (timer) clearTimeout(timer)
  timer = null
  firstRequestAt = 0
  pendingRun = null
}

/** Alias histórico que usan los specs. */
export const _resetSyncNudgeForTest = cancelSyncNudge
